import { Visibility } from '@zoocasa/go-search';
import { getGoSearchHost, isServerSide } from 'utils/host-config';
import { getVisibility } from './fetcher';

/**
 * Interface defining the API methods for retrieving visibility information for listings.
 * This API provides methods to fetch visibility data using different identifiers.
 */
export type VisibilityApiType = {
  /**
     * Retrieves visibility information for a listing using its slug.
     * @param slug The unique slug identifier for the listing
     * @param tenantId Optional tenant identifier for multi-tenant environments
     * @param signal Optional AbortSignal for request cancellation
     * @returns Promise resolving to a single {@link Visibility} object or undefined if not found.
     */
  getVisibilityBySlug(slug: string, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined>;

  /**
     * Retrieves visibility information for a listing using its ID.
     *
     * The visibility object will have the following properties:
     * - id: The numeric ID of the listing. The value matches the public.listings.id of the same property in the Database.
     * - mls: The MLS (Multiple Listing Service) identifier
     * - docId: The id for the property in the Elasticsearch index.
     * - listingKey: The key field from an MLS system that can be used to identify the property. See: https://ddwiki.reso.org/display/DDW17/ListingKey.
     * - slug: The slug identifier for the listing
     * - isVisible: Whether the property is visible to the user when accessed via the given tenant site.
     * - reason: The {@link RestrictionReason} the property is not visible to the user.
     * @param id The numeric ID of the listing
     * @param tenantId Optional tenant identifier. If not provided, the tenantId will be inferred from the request headers.
     * @param signal Optional AbortSignal for request cancellation
     * @returns Promise resolving to a single {@link Visibility} object or undefined if not found.
     */
  getVisibilityById(id: number, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined>;

  /**
     * Retrieves visibility information for a listing using its listing key.
     * @param listingKey The unique listing key identifier
     * @param tenantId Optional tenant identifier. If not provided, the tenantId will be inferred from the request headers.
     * @param signal Optional AbortSignal for request cancellation
     * @returns Promise resolving to a single Visibility object or undefined if not found.
     */
  getVisibilityByListingKey(listingKey: string, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined>;

  /**
     * Retrieves visibility information for a listing using its MLS number.
     * @param mls The MLS (Multiple Listing Service) identifier
     * @param tenantId Optional tenant identifier. If not provided, the tenantId will be inferred from the request headers.
     * @param signal Optional AbortSignal for request cancellation
     * @returns Promise resolving to a single Visibility object or undefined if not found.
     */
  getVisibilityByMls(mls: string, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined>;
};

class VisibilityApi implements VisibilityApiType {
  private constructor(private host: string) {}
  getVisibilityBySlug(slug: string, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined> {
    return getVisibility({ slug }, tenantId, signal, this.host);
  }
  async getVisibilityById(id: number, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined> {
    const visibility = await getVisibility({ id }, tenantId, signal, this.host);
    return visibility;
  }
  async getVisibilityByListingKey(listingKey: string, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined> {
    const visibility = await getVisibility({ listingKey }, tenantId, signal, this.host);
    return visibility;
  }
  async getVisibilityByMls(mls: string, tenantId?: string, signal?: AbortSignal): Promise<Visibility | undefined> {
    const visibility = await getVisibility({ mls }, tenantId, signal, this.host);
    return visibility;
  }

  static create(host: string = getGoSearchHost(isServerSide())): VisibilityApi {
    return new VisibilityApi(host);
  }
}

export const createVisibilityApi = VisibilityApi.create;